/**
 * Inline recovery actions for the designed tile states: an error body offers
 * Retry (+ Configure when the tab registered its handler on the configure
 * bridge); a policy-blocked body offers "Run now" — the SAME manual path as
 * the kebab's "Refresh now" (`runTileManually`, destructive-confirm capable),
 * so the execution-policy trust model is untouched. Renders nothing outside a
 * framed dashboard tile (no `TileChromeContext`) — those surfaces keep their
 * text-only states.
 */
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { isQueryBackedTile } from '../../types/dashboardTypes';
import { runTileManually } from '../../services/manualTileRun';
import { selectTileRuntime, useDashboardRuntimeStore } from '../../store/dashboardRuntimeStore';
import {
  selectTileConfigureHandler,
  useTileConfigureBridgeStore,
} from '../../store/tileConfigureBridgeStore';
import { useTileChrome } from './tileChromeContext';

const BUTTON_CLASS =
  'inline-flex h-6 items-center gap-1.5 rounded-md border border-border bg-card px-2 text-[11px] font-medium text-foreground hover:bg-muted disabled:pointer-events-auto disabled:opacity-50';

export function TileStateActions({ variant }: { variant: 'error' | 'policy' }) {
  const chrome = useTileChrome();
  const { track } = useAnalytics();
  const runtime = useDashboardRuntimeStore(
    selectTileRuntime(chrome?.dashboardId ?? '', chrome?.tile.id ?? ''),
  );
  const configure = useTileConfigureBridgeStore(
    selectTileConfigureHandler(chrome?.dashboardId ?? ''),
  );

  const tile = chrome?.tile;
  if (!chrome || !tile || !isQueryBackedTile(tile)) return null;

  const running = runtime?.status === 'running';
  const runLabel = variant === 'policy' ? 'Run now' : 'Retry';
  const onRun = async () => {
    const result = await runTileManually(chrome.dashboardId, tile);
    // Success-side firing (repo analytics rule) — mirrors dashboard_tile_refresh.
    if (result.ok) {
      track('feature:dashboard_tile_inline_run', { from: variant, tileType: tile.tileType });
    }
  };
  const onConfigure = () => {
    track('feature:dashboard_tile_inline_configure', { tileType: tile.tileType });
    configure?.(tile);
  };

  return (
    <div className="flex items-center gap-2 pt-1" data-testid="tile-state-actions">
      <button
        type="button"
        className={BUTTON_CLASS}
        disabled={running}
        title={running ? 'A run is already in progress' : undefined}
        onClick={() => void onRun()}
        data-testid={variant === 'policy' ? 'tile-run-now-button' : 'tile-retry-button'}
      >
        {running && <RandomDotmSpinner size={12} />}
        {runLabel}
      </button>
      {variant === 'error' && configure && (
        <button
          type="button"
          className={BUTTON_CLASS}
          onClick={onConfigure}
          data-testid="tile-configure-button"
        >
          Configure…
        </button>
      )}
    </div>
  );
}
